import React, { useState } from 'react';

const BookCard = ({ book }) => {
  const [imgError, setImgError] = useState(false);
  const isIChing = book.title.includes("I Ching") || book.id === "i_ching";
  const isStratagems = book.id === "thirty_six_stratagems" || book.title.includes("Thirty-Six Stratagems");

  // Define theme colors based on tags (Fallback for when image fails)
  const getThemeColor = (tags, bookId, title) => {
    // Dark Grey/Silver theme for 36 Stratagems (Hidden Wisdom) - check first
    if (bookId === "thirty_six_stratagems" || title.includes("Thirty-Six Stratagems")) {
      return "bg-slate-800 border-slate-600 text-slate-200";
    }
    
    if (!tags || !Array.isArray(tags)) return "bg-gray-900 border-gray-700 text-gray-100";
    
    const tagString = tags.join(' ').toLowerCase();
    if (tagString.includes("#nature") || tagString.includes("#zen")) return "bg-emerald-900 border-emerald-700 text-emerald-100";
    if (tagString.includes("#strategy") || tagString.includes("#business")) return "bg-slate-900 border-slate-700 text-slate-100";
    if (tagString.includes("#romance")) return "bg-rose-900 border-rose-700 text-rose-100";
    if (tagString.includes("#divination")) return "bg-purple-900 border-purple-700 text-purple-100";
    return "bg-gray-900 border-gray-700 text-gray-100"; // Default
  };

  const themeClass = getThemeColor(book.tags || [], book.id, book.title);
  
  // Special Glow Effect for I Ching
  const glowEffect = isIChing 
    ? "hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] hover:border-purple-400 transition-all duration-500" 
    : "hover:border-gray-400 transition-colors duration-300";

  // Special Glow Effect for 36 Stratagems (Dark Grey/Silver)
  const stratagemsGlow = isStratagems 
    ? "hover:shadow-[0_0_20px_rgba(148,163,184,0.4)] hover:border-slate-500 transition-all duration-500" 
    : "";

  return (
    <a 
      href={book.amazon_link} 
      target="_blank" 
      rel="noopener noreferrer"
      className={`group relative flex flex-col h-full rounded-xl overflow-hidden border border-gray-800 bg-gray-950 ${glowEffect} ${stratagemsGlow}`}
      style={{ minHeight: '100%' }}
    >
      {/* 1. COVER IMAGE AREA */}
      <div className="relative w-full aspect-[2/3] overflow-hidden bg-gray-900">
        {!imgError && book.cover_image ? (
          <img 
            src={book.cover_image} 
            alt={book.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            onError={() => setImgError(true)} 
          />
        ) : (
          // Fallback: CSS Style Cover (Only shows if image fails or is missing)
          <div className={`w-full h-full flex flex-col justify-center items-center p-6 text-center ${themeClass}`}>
            <div className="border-l-2 border-white/20 h-full absolute left-4 top-0 bottom-0"></div>
            <h3 className="font-serif text-2xl font-bold text-white/90 tracking-widest" style={{ writingMode: 'vertical-lr' }}>
              {book.title.split(" ")[0]}
            </h3>
          </div>
        )}
        
        {/* Overlay Gradient for text readability at bottom */}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent opacity-90"></div>
        
        {/* I Ching Sparkle Badge */}
        {isIChing && (
          <div className="absolute top-2 right-2 bg-purple-600/80 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full border border-purple-400 shadow-lg animate-pulse">
            ✨ Oracle
          </div>
        )}
      </div>

      {/* 2. TEXT CONTENT AREA */}
      <div className="flex-1 p-5 flex flex-col justify-between relative z-10 -mt-12">
        <div>
          {/* Tags */}
          {book.tags && book.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {book.tags.map(tag => (
                <span key={tag} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-white/10 text-white/70 border border-white/5">
                  {tag}
                </span>
              ))}
            </div>
          )}
          
          <h3 className="font-serif text-xl text-white mb-1 flex items-center gap-2">
            {book.title}
            {isIChing && <span className="text-purple-400">✨</span>}
          </h3>
          {book.subtitle && (
            <p className="text-xs text-gray-400 font-mono mb-4 uppercase tracking-widest">{book.subtitle}</p>
          )}
          
          {book.one_liner && (
            <p className="text-sm text-gray-300 leading-relaxed italic opacity-90">
              "{book.one_liner}"
            </p>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-800/50 flex items-center justify-between">
          <span className="text-xs text-gray-500">Why read?</span>
          <span className="text-xs text-white/80 group-hover:text-red-400 transition-colors flex items-center gap-1">
            Amazon <span className="text-lg">→</span>
          </span>
        </div>
      </div>
    </a>
  );
};

export default BookCard;
